import usePageTitle from "system/hooks/use-page-title";
import ActionButtons from "components/ui/ActionButtons";
import EndpointFilter from "components/ui/EndpointFilter";
import { InfoIcon } from "components/ui/Icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useMultiFilters, { hasAnyFilter } from "system/hooks/use-multi-filters";
import { SubjectStatusSubjectSummary, SubjectStatusTime } from "system/types/wireTypes";
import useChartRange from "system/hooks/use-chart-range";
import useEndpointCounts from "system/hooks/use-endpoint-counts";
import { getUtcOffset, toDate, toFormatedDate, toLocalISOString } from "system/libraries/utilities";
import TheMainChart from "components/ui/TheMainChart";
import useUrlSearch from "system/hooks/use-url-search";
import EndpointsBySubjectTable from "components/ui/EndpointsBySubjectTable";
import useConfiguration from "system/hooks/use-configuration";
import AccessDeniedPage from "./system/AccessDeniedPage";
import LoadingPage from "./system/LoadingPage";
import useApi from "system/hooks/use-api";
import ExportToCsvButton from "components/ui/ExportToCsvButton";
import { Dropdown, ResponsiveMode } from "@fluentui/react";
import { fluentUiDarkTheme, fluentUiTitleDropdownStyle } from "system/styles/fluent-ui-classes";
import { useDispatch, useSelector } from "react-redux";
import IRootState from "system/types/store/IRootState";
import { mainActions } from "system/store/slices/main-slice";
import { Loading } from "components/ui";
import { useLocation } from "react-router-dom";
import { RetryErrorPage } from "./system";
import { IIconData } from "system/types/interfaces/IIconData";

let chartUpdateRefresh: NodeJS.Timeout;

const MainPage = () => {
  const api = useApi();
  const [selectedDateValue, setSelectedDateValue] = useState<string>("");
  const [selectedDateFormatted, setSelectedDateFormatted] = useState<string | null>(null);
  const { currentTenant } = useConfiguration();
  const [mainChartData, setMainChartData] = useState<SubjectStatusTime[] | null>(null);
  const [endpointsBySubjectData, setEndpointsBySubjectData] = useState<SubjectStatusSubjectSummary | null>(null);
  const [chartRefresh, setChartRefresh] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [selectedDateString, setSelectedDateString] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState<string>("Loading data...");
  const { machineFilterSet } = useMultiFilters();
  const [chartError, setChartError] = useState<string | null>(null);
  const [gridError, setGridError] = useState<string | null>(null);
  const dates = useChartRange();
  const urlSearch = useUrlSearch();
  const dispatch = useDispatch();
  const endpointCounts = useSelector((state: IRootState) => state.main.endpointCounts);
  const endpointCountsRef = useRef(endpointCounts);
  endpointCountsRef.current = endpointCounts;

  const location = useLocation();

  const selectedGraphBy = useMemo(() => (urlSearch.get("graphBy")?.toLowerCase() === "subject" ? "subject" : "endpoint"), [urlSearch]);

  usePageTitle({
    title: currentTenant?.siteName ?? "",
    subTitle: currentTenant?.siteName?.length ? "Company" : "",
  });

  useEffect(() => {
    if (!endpointsBySubjectData?.bySubject) {
      return;
    }

    const icons: IIconData[] = endpointsBySubjectData.bySubject.map((item) => ({
      subject: item.subject ?? "",
      iconUrl: item.iconUrl,
    }));

    dispatch(mainActions.setIconData(icons));
  }, [endpointsBySubjectData, currentTenant, dispatch]);

  useEffect(() => {
    const selectedDateUrlValue = urlSearch.get("selectedDate");

    if (selectedDateUrlValue) {
      if (selectedDateUrlValue !== selectedDateValue) {
        setSelectedDateValue(selectedDateUrlValue);
        setSelectedDateString(selectedDateUrlValue);
      }
      return;
    }

    if (!dates?.length) {
      setSelectedDateString(null);
      return;
    }

    const resultDate = toLocalISOString(dates[dates.length - 1]);
    setSelectedDateValue(resultDate);
    setSelectedDateString(resultDate);
  }, [selectedDateValue, dates, urlSearch]);

  // Updates chart (scheduled every 10 minutes)
  const updateMainChartData = useCallback((signal?: AbortSignal) => {
    setChartError(null);

    if (!dates || !currentTenant) {
      setMainChartData(null);
      setEndpointsBySubjectData(null);
      return;
    }

    setTimeout(() => {
      setLoadingMessage("Filtering the results...");
    }, 8000);

    setTimeout(() => {
      setLoadingMessage("Calculating values by Subject...");
    }, 16000);

    const fetchData = async () => {
      try {
        const data = await api.summary.subjectStatus(
          {
            breakoutOption: 0,
            filters: machineFilterSet,
            graphBy: selectedGraphBy,
            referenceTimes: dates.map((x) => x.toISOString()),
          },
          {
            retries: 3,
            baseDelay: 1000,
            maxDelay: 10000,
            signal,
          }
        );
        if (data && data.isSuccess && data?.byBreakoutValue && data.byBreakoutValue.length > 0) {
          setMainChartData(data?.byBreakoutValue[0].byTime);
          const totalCount = endpointCountsRef.current?.totalReporting;
          setLoadingMessage(totalCount ? `Getting information for ${totalCount.toLocaleString()} machines...` : `Getting information...`);
          setChartError(null);
        } else {
          setChartError(data?.failMessage || "Failed to load endpoint status data after multiple attempts.");
        }

        chartUpdateRefresh = setTimeout(() => {
          setChartRefresh(!chartRefresh);
        }, 600000);
      } catch (error: any) {
        if (error.name === "AbortError") return;
        console.error("Error fetching endpoint status data:", error);
        setChartError(error.message || "Failed to load endpoint status data after multiple attempts.");
      }
    };

    fetchData();
  }, [api.summary, chartRefresh, currentTenant, dates, machineFilterSet, selectedGraphBy]);

  const updateEndpointsBySubjectTable = useCallback(async (signal?: AbortSignal) => {
    setGridError(null);

    if (!selectedDateString || !dates) {
      return;
    }

    setEndpointsBySubjectData(null);

    try {
      const data = await api.summary.subjectStatusSubject(
        {
          filters: machineFilterSet,
          fromTime: selectedDateString ? new Date(selectedDateString) : null,
        },
        {
          retries: 3,
          baseDelay: 1000,
          maxDelay: 10000,
          signal,
        }
      );

      if (data?.isSuccess) {
        setEndpointsBySubjectData(data);
        setSelectedDateFormatted(toFormatedDate(selectedDateString!.replace("Z", "")));
        setGridError(null);
      } else {
        setSelectedDateFormatted(null);
        setGridError(data?.failMessage || "Failed to load endpoint status data after multiple attempts.");
      }
    } catch (error: any) {
      if (error.name === "AbortError") return;
      console.error("Error fetching endpoint status data:", error);
      setGridError(error.message || "Failed to load endpoint status data after multiple attempts.");
    }
  }, [api.summary, dates, machineFilterSet, selectedDateString]);

  const isDefaultFilters = useMemo(() => !hasAnyFilter(machineFilterSet), [machineFilterSet]);

  const isDefaultDate = useMemo(() => {
    if (!selectedDateString) return true;
    const today = toLocalISOString(new Date()).split("T")[0];
    const selectedDate = selectedDateString.split("T")[0];
    return selectedDate === today;
  }, [selectedDateString]);

  const shouldUseCachedData = useMemo(() => {
    const isDefaultGraphBy = selectedGraphBy === "endpoint";
    return isDefaultFilters && isDefaultGraphBy && isDefaultDate && !forceRefresh && currentTenant?.isCacheDataEnabled;
  }, [selectedGraphBy, isDefaultFilters, isDefaultDate, forceRefresh, currentTenant?.isCacheDataEnabled]);

  // Fetch cached data on initial load when default filters are applied
  useEffect(() => {
    if (!currentTenant || !selectedDateString) {
      return;
    }

    const controller = new AbortController();

    if (shouldUseCachedData) {
      const fetchData = async () => {
        try {
          const data = await api.summary.cachedData({
            retries: 3,
            baseDelay: 1000,
            maxDelay: 10000,
            signal: controller.signal,
          });
          if (data?.graphData && data?.tableData) {
            const graphData = data.graphData;
            const tableData = data.tableData;

            const cachedByTime = graphData?.byBreakoutValue?.[0]?.byTime;
            if (cachedByTime && cachedByTime.length === 16 && dates && dates.length === 16) {
              const cachedDays = cachedByTime.map((time) => new Date(time.referenceJsTime).toISOString().split("T")[0]);
              const rangeDays = dates.map((date) => date.toISOString().split("T")[0]);

              if (cachedDays.join(",") !== rangeDays.join(",")) {
                setForceRefresh(true);
                return;
              }
            }

            if (graphData?.byBreakoutValue?.[0]?.byTime) {
              setMainChartData(graphData.byBreakoutValue[0].byTime);
            } else {
              setMainChartData(null);
            }

            if (tableData) {
              setEndpointsBySubjectData(tableData);
              setSelectedDateFormatted(toFormatedDate(toLocalISOString(new Date(data.assessmentTime)).replace("Z", "")));
            } else {
              setEndpointsBySubjectData(null);
              setSelectedDateFormatted(null);
            }
          } else {
            updateMainChartData(controller.signal);
            updateEndpointsBySubjectTable(controller.signal);
          }
        } catch (error: any) {
          if (error.name === "AbortError") return;
          console.error("Error fetching cached compliance data:", error);
          setMainChartData(null);
          setEndpointsBySubjectData(null);

          updateMainChartData(controller.signal);
          updateEndpointsBySubjectTable(controller.signal);
        }
      };

      fetchData();
    } else {
      dispatch(mainActions.setFilteredEndpointCount(null));
      updateMainChartData(controller.signal);
      updateEndpointsBySubjectTable(controller.signal);
    }

    return () => {
      controller.abort();
      clearTimeout(chartUpdateRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, isDefaultFilters, currentTenant, shouldUseCachedData, selectedDateString, updateMainChartData, updateEndpointsBySubjectTable]);

  const exportData = useMemo(() => {
    return {
      fileName: `Aiden Endpoints by Subject ${toDate(selectedDateValue)}`,
      source:
        endpointsBySubjectData?.bySubject?.map((x) => ({
          Subject: x.subject,
          Critical: x.nError,
          NotReporting: x.nUnknown,
          HasRecommendations: x.nNoncompliant,
          CompliantPendingReboot: x.nCompliantAfterReboot,
          Compliant: x.nCompliant,
        })) ?? [],
    };
  }, [endpointsBySubjectData, selectedDateValue]);

  useEffect(() => {
    if (!endpointsBySubjectData || !currentTenant) {
      return;
    }

    const filteredCount = !endpointsBySubjectData?.statusTime
      ? 0
      : endpointsBySubjectData.statusTime.nError +
        endpointsBySubjectData.statusTime.nUnknown +
        endpointsBySubjectData.statusTime.nNoncompliant +
        endpointsBySubjectData.statusTime.nCompliant +
        endpointsBySubjectData.statusTime.nCompliantAfterReboot;

    dispatch(mainActions.setFilteredEndpointCount(filteredCount));
  }, [currentTenant, dispatch, endpointsBySubjectData]);

  useEndpointCounts(selectedDateString);

  const refreshHandler = useCallback(() => {
    setChartError(null);
    setGridError(null);
    setMainChartData(null);
    setEndpointsBySubjectData(null);
    setForceRefresh(true);
    setChartRefresh(!chartRefresh);
  }, [chartRefresh]);

  const graphByChangeHandler = useCallback(
    (key?: string) => {
      urlSearch.set("graphBy", key?.toLowerCase() ?? null);
    },
    [urlSearch]
  );

  useEffect(() => {
    if (location.state?.forceRefresh) {
      setForceRefresh(false);
    }
  }, [location.state]);

  if (!currentTenant) {
    return <AccessDeniedPage />;
  }

  const dropdownOptions = [
    { key: "endpoint", text: "Endpoint Compliance" },
    { key: "subject", text: "Product Compliance" },
  ];

  if (!mainChartData && !chartError && !endpointsBySubjectData && !gridError) return <LoadingPage />;

  return (
    <>
      <ActionButtons timespanButton={true} onRefresh={refreshHandler}>
        <ExportToCsvButton items={[exportData]} />
      </ActionButtons>
      <EndpointFilter showTotals />

      <div className="main-chart">
        <div className="card">
          <div className="card-body">
            {chartError ? (
              <RetryErrorPage errorMessage="Failed to load chart data" refreshHandler={updateMainChartData} />
            ) : mainChartData ? (
              <>
                <span className="endpoint-head d-flex align-items-center gap-1">
                  {currentTenant.isSubjectStatusEnabled ? (
                    <Dropdown
                      dropdownWidth={300}
                      selectedKey={selectedGraphBy ? selectedGraphBy : "endpoint"}
                      onChange={(_, item) => graphByChangeHandler(item?.key?.toString())}
                      responsiveMode={ResponsiveMode.xxxLarge}
                      styles={fluentUiTitleDropdownStyle}
                      options={dropdownOptions}
                      theme={fluentUiDarkTheme}
                    />
                  ) : (
                    dropdownOptions[0].text
                  )}
                  <InfoIcon
                    text={`${
                      selectedGraphBy === "endpoint"
                        ? "Status of each endpoint based on full compliance with your desired state policy."
                        : "Status of all required products and tasks on all endpoints.  "
                    }`}
                  />{" "}
                </span>
                <TheMainChart data={mainChartData} selectedGraphBy={selectedGraphBy} />
                {selectedDateFormatted && (
                  <div className="summary-number">
                    <div className="d-flex justify-content-between gap-3 align-items-center">
                      <p className="mb-0">
                        <span className="endpoint-head">
                          {" "}
                          Endpoints by subject{" "}
                          <InfoIcon text="For a given subject, a count of endpoints in where the subject is in a given compliance state." />
                        </span>
                      </p>
                      <p className="d-flex flex-column flex-md-row gap-1 gap-md-2 align-items-md-center mb-0">
                        <span className="lh-sm"> Selected date and time</span>
                        <span className="lh-sm">{selectedDateFormatted} (Browser local time UTC{getUtcOffset(new Date(selectedDateFormatted))})</span>
                      </p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <LoadingPage />
            )}

            {gridError ? (
              <RetryErrorPage errorMessage="Failed to load grid data" refreshHandler={updateEndpointsBySubjectTable} />
            ) : endpointsBySubjectData?.isSuccess ? (
              <div className="machine-table scroll" style={{ maxHeight: "65vh", marginTop: "16px", paddingRight: "10px" }}>
                <EndpointsBySubjectTable data={endpointsBySubjectData} selectedDateString={selectedDateString} />
              </div>
            ) : (
              <Loading message={loadingMessage} />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MainPage;
